// ============================================================================
// Redacted Logger — secret-stripping wrapper around `console`.
//
// Mounted into `SocialPostingCallCtx.redactedLogger` and re-used by future
// capabilities that touch raw access tokens (#788, #789, #790). The
// implementation must:
//   - Match the canonical secret patterns (Bearer tokens, OAuth params, raw
//     Meta tokens) so adapter authors don't need to reason about which
//     surface logs the value.
//   - Replace matches with `***redacted***` so the value never appears in
//     Worker tail logs / dashboards.
//   - Preserve message structure (prefix, JSON shape) so debugging stays
//     useful.
//
// The shape mirrors `SocialPostingRedactedLogger` (capabilities/social-
// posting.ts). Both `msg` (string) and `meta` (object) are sanitised; meta
// values are walked recursively so adapters can pass arbitrary FB error
// envelopes without leaking tokens. Strings that LOOK like a Meta access
// token (length >= 40, base64url-ish charset) are wholesale replaced.
// ============================================================================

import type { SocialPostingRedactedLogger } from '../capabilities/social-posting'

// ============================================================================
// Patterns
// ============================================================================

/**
 * Patterns that match common secret-bearing substrings. The capture group is
 * preserved so the surrounding context (`access_token=`) survives, only the
 * value is replaced.
 *
 * Keep in sync with `adapters/facebook-messenger/oauth.ts → SECRET_PATTERNS`
 * and `adapters/facebook-messenger/types.ts → sanitizeFacebookErrorMessage`.
 */
const SECRET_PATTERNS: ReadonlyArray<RegExp> = [
  /(access_token=)[^&\s"']+/gi,
  /(client_secret=)[^&\s"']+/gi,
  /(fb_exchange_token=)[^&\s"']+/gi,
  /(refresh_token=)[^&\s"']+/gi,
  /(Authorization:\s*Bearer\s+)[A-Za-z0-9._\-|]+/gi,
  /(Bearer\s+)[A-Za-z0-9._\-|]+/gi,
]

/**
 * Threshold above which any base64url-shaped string is treated as a likely
 * Meta token even when no `access_token=` prefix is present. 40 chars is
 * conservative — short FB access tokens are ~25 chars, full long-lived ones
 * are 100+. The 40 floor avoids touching ULIDs (26 chars), short hashes, or
 * UUIDs (36 chars but contain `-` separators).
 */
const TOKEN_HEURISTIC_MIN_LENGTH = 40

/**
 * Charset for the heuristic — matches FB/JWT-style tokens.
 *
 * Allows an optional `|<app_id>` suffix so real Meta page access tokens
 * (shaped `EAAxxx|123456789`) are redacted as a single unit. The standalone
 * tokens fall back to the 40-char threshold; tokens with a pipe must still
 * have ≥40 chars in the prefix before the pipe — that's enough to discriminate
 * from short `|`-bearing strings like `key|val` (~7 chars) appearing in
 * non-secret logs. The trailing app-id can be as short as 6 digits, so we
 * don't enforce a length on the suffix.
 */
const TOKEN_HEURISTIC_REGEX = /\b[A-Za-z0-9_\-]{40,}(?:\|[A-Za-z0-9_\-]+)?\b/g

const REDACTED = '***redacted***'

// ============================================================================
// Sanitisation
// ============================================================================

/**
 * Strip secrets from a string. Applies all `SECRET_PATTERNS` and then the
 * length-based heuristic.
 *
 * Exported so adapter modules can sanitise log lines that take a path other
 * than `redactedLogger` (e.g. captured error messages re-thrown across the
 * capability boundary).
 */
export function redactString(raw: string): string {
  if (typeof raw !== 'string' || raw.length === 0) return raw
  let out = raw
  for (const pattern of SECRET_PATTERNS) {
    out = out.replace(pattern, `$1${REDACTED}`)
  }
  // Heuristic pass — replace anything that looks like a standalone Meta
  // token. Must run AFTER the prefix-aware patterns so `access_token=EAAxxx`
  // doesn't double-replace.
  out = out.replace(TOKEN_HEURISTIC_REGEX, (match) => {
    // Whitelist common harmless ids that happen to be long: hex of 40+ chars
    // (sha1 hashes) or all-digits. They aren't secrets.
    if (/^[0-9a-f]+$/i.test(match)) return match
    if (/^[0-9]+$/.test(match)) return match
    return REDACTED
  })
  return out
}

/**
 * Walk an arbitrary structure and sanitise every leaf string. Cyclic refs are
 * skipped to keep this safe against accidental adapter mistakes.
 */
export function redactMeta(value: unknown, seen: WeakSet<object> = new WeakSet()): unknown {
  if (value === null || value === undefined) return value
  if (typeof value === 'string') return redactString(value)
  if (typeof value !== 'object') return value
  if (seen.has(value as object)) return '[circular]'
  seen.add(value as object)
  if (Array.isArray(value)) {
    return value.map((item) => redactMeta(item, seen))
  }
  const out: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    // Object keys named like a token field get nuked wholesale — the value
    // is presumed to be the secret itself regardless of shape.
    if (
      /^(access_token|refresh_token|client_secret|fb_exchange_token|page_access_token|user_access_token|appsecret_proof|code|verifier)$/i.test(
        k,
      )
    ) {
      out[k] = REDACTED
      continue
    }
    out[k] = redactMeta(v, seen)
  }
  return out
}

// ============================================================================
// createRedactedLogger
// ============================================================================

export interface CreateRedactedLoggerOpts {
  /** Prefix prepended to every line (e.g. `[connectors/facebook-page]`). */
  prefix?: string
  /**
   * Sink overrides for tests. Defaults to `console` — each method is the
   * matching `console.*` member so log levels stay accurate.
   */
  sinks?: {
    debug?: (msg: string, meta?: Record<string, unknown>) => void
    info?: (msg: string, meta?: Record<string, unknown>) => void
    warn?: (msg: string, meta?: Record<string, unknown>) => void
    error?: (msg: string, meta?: Record<string, unknown>) => void
  }
}

/**
 * Build a `SocialPostingRedactedLogger` whose every line passes through
 * `redactString` / `redactMeta` before reaching the sink. Adapters MUST log
 * through this; raw `console.*` would dump tokens straight into Workers tail
 * logs, and the linter blocks new `console` references inside adapter files.
 */
export function createRedactedLogger(
  opts: CreateRedactedLoggerOpts = {},
): SocialPostingRedactedLogger {
  const prefix = opts.prefix ? `${opts.prefix} ` : ''
  // Fall through to console; tests inject sinks for assertion.
  const sinks = opts.sinks ?? {}
  const defaultSink = {
    debug: (m: string, meta?: Record<string, unknown>) =>
      meta ? console.debug(m, meta) : console.debug(m),
    info: (m: string, meta?: Record<string, unknown>) =>
      meta ? console.info(m, meta) : console.info(m),
    warn: (m: string, meta?: Record<string, unknown>) =>
      meta ? console.warn(m, meta) : console.warn(m),
    error: (m: string, meta?: Record<string, unknown>) =>
      meta ? console.error(m, meta) : console.error(m),
  }

  function emit(
    level: 'debug' | 'info' | 'warn' | 'error',
    msg: string,
    meta?: Record<string, unknown>,
  ): void {
    const sanitisedMsg = `${prefix}${redactString(msg)}`
    const sanitisedMeta =
      meta && typeof meta === 'object'
        ? (redactMeta(meta) as Record<string, unknown>)
        : undefined
    const sink = sinks[level] ?? defaultSink[level]
    if (sanitisedMeta !== undefined) {
      sink(sanitisedMsg, sanitisedMeta)
    } else {
      sink(sanitisedMsg)
    }
  }

  return {
    debug: (msg, meta) => emit('debug', msg, meta),
    info: (msg, meta) => emit('info', msg, meta),
    warn: (msg, meta) => emit('warn', msg, meta),
    error: (msg, meta) => emit('error', msg, meta),
  }
}
